import { useCallback } from 'react'
import { validateEngineSchedules, hasValidationErrors } from '../validation/save'
import { applyLocksForSave, collectInstrumentPinning, saveEngineSchedules, readEngineSchedules } from '../api/engineSchedulesApi'
import { buildScheduleLabel } from '../summaries/plainLanguage'
import { createEmptySchedule } from '../models/createEmptySchedule'
import { useScheduleActions } from './useScheduleActions'
import { setSchedulingEngineDraft } from 'routes/Studies/routes/Study/routes/Instruments/routes/Instrument/modules/Instrument'
import { actions as notyActions } from 'layouts/ErrorBox'
import { generateNotyMessage } from 'utils/request'

export const useScheduleSave = ({ studyID, instrument, entityOptions, dispatch, hasSchedulingEngine }) => {
  const { schedules, activeScheduleId, setErrors, setSaving, hydrateSchedules } = useScheduleActions()

  const persistSchedules = useCallback(
    async schedulesToSave => {
      const instrumentPinningMap = await collectInstrumentPinning({
        studyID,
        schedules: schedulesToSave,
        entityLookup: entityOptions,
        dispatch,
      })
      const lockedSchedules = applyLocksForSave(schedulesToSave, entityOptions, instrumentPinningMap).map(schedule => ({
        ...schedule,
        label: schedule.label || buildScheduleLabel(schedule),
        name: schedule.name || schedule.label || buildScheduleLabel(schedule),
      }))
      const errors = validateEngineSchedules(lockedSchedules, entityOptions)
      if (hasValidationErrors(errors)) {
        setErrors(errors)
        const firstMessage = Object.values(errors).find(Boolean)
        dispatch(
          notyActions.showError({
            text: generateNotyMessage(
              firstMessage
                ? `Fix the highlighted schedule fields before saving. ${firstMessage}`
                : 'Fix the highlighted schedule fields before saving.',
              false,
            ),
          }),
        )
        if (typeof window !== 'undefined') {
          window.requestAnimationFrame(() => {
            document.querySelector('.scheduling-engine .se-error[role="alert"]')?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
          })
        }
        return null
      }
      setErrors({})
      const nextInstrument = await dispatch(
        saveEngineSchedules({
          studyID,
          instrument,
          schedules: lockedSchedules,
          hasSchedulingEngine,
          activeScheduleId,
        }),
      )
      return nextInstrument
    },
    [
      activeScheduleId,
      dispatch,
      entityOptions,
      hasSchedulingEngine,
      instrument,
      setErrors,
      studyID,
    ],
  )

  const syncDraftFromInstrument = useCallback(
    (nextInstrument, activeId) => {
      const { schedules: savedSchedules } = readEngineSchedules(nextInstrument)
      if (!savedSchedules.length) return
      const resolvedActive =
        activeId !== undefined && activeId !== null
          ? activeId
          : activeScheduleId && savedSchedules.some(schedule => schedule.id === activeScheduleId)
          ? activeScheduleId
          : savedSchedules[0]?.id
      hydrateSchedules(savedSchedules, resolvedActive)
      dispatch(
        setSchedulingEngineDraft({
          instrumentId: nextInstrument?.id,
          schedules: savedSchedules,
          activeScheduleId: resolvedActive,
        }),
      )
    },
    [activeScheduleId, dispatch, hydrateSchedules],
  )

  const onSave = useCallback(async () => {
    setSaving(true)
    try {
      const nextInstrument = await persistSchedules(schedules)
      if (nextInstrument) {
        syncDraftFromInstrument(nextInstrument)
      }
      return !!nextInstrument
    } finally {
      setSaving(false)
    }
  }, [persistSchedules, schedules, setSaving, syncDraftFromInstrument])

  const onSaveAndAdd = useCallback(async () => {
    setSaving(true)
    try {
      const newSchedule = createEmptySchedule()
      const schedulesToSave = [...schedules, newSchedule]
      const nextInstrument = await persistSchedules(schedulesToSave)
      if (!nextInstrument) return false
      const savedSchedules = readEngineSchedules(nextInstrument).schedules
      const activeId = savedSchedules[savedSchedules.length - 1]?.id || newSchedule.id
      syncDraftFromInstrument(nextInstrument, activeId)
      return true
    } finally {
      setSaving(false)
    }
  }, [persistSchedules, schedules, setSaving, syncDraftFromInstrument])

  return { onSave, onSaveAndAdd }
}
